import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Fingerprint, BookOpen, Sparkles, RefreshCw, Mic, Headphones, Network, Trophy, Users,
  Smartphone, ArrowRight, Check, X, Brain,
} from 'lucide-react';
import { Accordion } from '../components/ui/Accordion';
import { Button } from '../components/ui/Button';
import { RidgeTexture } from '../components/ui/RidgeTexture';
import {
  Aurora, MagneticCard, GlowBorder, SectionDivider, Reveal, StaggerGroup,
} from '../components/ui/depth';
import { fadeUpItem, fadeRightItem, fadeLeftItem, scaleItem } from '../components/ui/depth-motion';
import { Footer } from '../components/Footer';

const fullFaq = [
  { question: 'What is CogniPrint?', answer: 'CogniPrint is an AI-powered adaptive learning platform. It maps how you learn (your CogniPrint profile), transforms your uploaded content into a personalised learning experience, and provides a contextual AI Tutor that adapts to your profile. Think of it as a study partner that understands how your brain works.' },
  { question: 'How does the CogniPrint diagnostic work?', answer: 'We offer a quick 2-minute diagnostic (free) that maps your learning preferences across 4 core dimensions, and a full 5-minute diagnostic (paid tiers) that adds 4 advanced dimensions — things like how you absorb information, how deeply you need to understand material, what motivates you, and more. There are no wrong answers. The result is your CogniPrint profile, which shapes everything the platform builds for you.' },
  { question: 'What content can I upload?', answer: 'PDFs and Word documents up to 50MB. Textbook chapters, study guides, professional standards, training manuals, course notes — anything you need to learn from. Each upload is processed by AI to extract key concepts and build a structured knowledge graph.' },
  { question: 'Is my content safe and private?', answer: 'Yes. Your uploaded content is processed by AI to generate your learning experience but is not stored permanently or used to train AI models. CogniPrint is built with privacy by design, compliant with both POPIA (South Africa) and GDPR (Europe). Your content stays yours.' },
  { question: 'How much does it cost?', answer: 'Your quick CogniPrint profile is free — always. The 2-minute diagnostic costs nothing and gives you genuine insight into how you learn across 4 core dimensions. Upgrade to unlock all 8 dimensions. For the full experience — content uploads, AI Tutor, and personalised learning paths — plans start at $9.99/month. See our pricing page for full details.' },
  { question: 'Does it work on mobile?', answer: 'Yes. CogniPrint is fully responsive on mobile browsers, and a native app is in development. You can study on your phone during your commute, review concept maps between classes, and chat with your AI Tutor anywhere.' },
  { question: 'Who is CogniPrint for?', answer: 'Anyone who needs to learn complex material. University students preparing for exams. Professionals studying for certifications (APC, CFA, board exams). Self-directed learners building new skills. Corporate teams that need personalised training. If you\'re studying, CogniPrint is for you.' },
  { question: 'How is this different from ChatGPT or NotebookLM?', answer: 'Those tools give everyone the same output regardless of how they learn. CogniPrint starts with your CogniPrint profile — a map of how your brain processes information — and builds every learning experience around it. The AI Tutor knows your content, your profile, and where you are in the material. It\'s not a generic chatbot. It\'s a personalised study partner.' },
  { question: 'What is CogniPrint recalibration and how often does it happen?', answer: 'Every two weeks, CogniPrint automatically reviews how you have actually studied — which concepts moved to mastery, which Tutor modes you relied on most, how your engagement shifted — and updates your CogniPrint profile. You receive a short monthly report that explains what changed and what it means for the next month of study. Recalibration is included in Pro and Teams. Learner-tier members see a preview around Day 14 and are invited to upgrade if they want ongoing recalibration.' },
  { question: 'Can CogniPrint handle audio lectures and recordings?', answer: 'Yes. You can upload common audio formats (MP3, WAV, M4A, OGG, FLAC, WebM, AAC) up to two hours per file, or record directly in the app. Transcription runs on your device when the local engine is available, so your audio does not leave your hardware. Once transcribed, the content enters the same AI pipeline as your PDFs.' },
  { question: 'Can I talk to the AI Tutor with my voice?', answer: 'Yes. All five Tutor interaction modes — Explain, Go Deeper, Test Me, Connect, and Simplify — support voice. You can speak a question and hear the answer back. The Tutor adapts its pace and length to your CogniPrint. Voice is entirely optional, every exchange is mirrored as text, and captions are available.' },
  { question: 'What is the knowledge graph and how is it different from a flashcard deck?', answer: 'Flashcards treat every fact as an isolated card. The CogniPrint knowledge graph treats your material as a network: nodes are concepts, edges are relationships. A gold ring fills in around each node as you are tested on it. The graph spans every document you upload and flags where two sources disagree.' },
  { question: 'How is gamification handled if I do not respond to points and streaks?', answer: 'CogniPrint\'s diagnostic measures your motivational driver. If you score as intrinsically motivated, the XP counter and leaderboard are quiet by default and the interface emphasises mastery of the knowledge graph rather than points. Extrinsically motivated learners see the full gamification layer.' },
  { question: 'Is there a human coach I can work with?', answer: 'Yes. The Coach add-on ($7.99/month on top of Pro) gives you a bi-weekly written recalibration review, unlimited ad-hoc check-ins, and guidance tied directly to your CogniPrint.' },
];

type Step = {
  icon: typeof Fingerprint;
  number: string;
  overline: string;
  title: string;
  body: string;
  visual: React.ReactNode;
  accent: 'indigo' | 'gold' | 'mixed';
};

/* ─── HERO ─── */
function HeroSection() {
  return (
    <section className="relative pt-40 pb-20 px-4 overflow-hidden">
      <Aurora palette="mixed" intensity={0.9} />
      <div className="max-w-4xl mx-auto text-center relative">
        <Reveal>
          <span className="inline-flex items-center gap-2 mb-5 px-3 py-1 rounded-pill text-xs font-semibold bg-indigo-500/12 text-indigo-300 border border-indigo-500/30">
            How CogniPrint works
          </span>
        </Reveal>
        <Reveal>
          <h1 className="font-heading font-bold text-4xl sm:text-5xl leading-[1.1] tracking-tight mb-6">
            Your textbook becomes a learning experience{' '}
            <span className="bg-gradient-to-r from-indigo-400 to-gold-500 bg-clip-text text-transparent">built for your brain.</span>
          </h1>
        </Reveal>
        <Reveal>
          <p className="text-content-secondary font-body text-lg max-w-2xl mx-auto leading-relaxed">
            Three steps. A profile that adapts every two weeks. An AI tutor shaped by how you actually think. And a Coach add-on for when you want a human in the loop.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

/* ─── EXPANDED 3 STEPS — alternating layouts ─── */
function StepsSection() {
  const steps: Step[] = [
    {
      icon: Fingerprint, number: '01', accent: 'indigo',
      overline: 'STEP 1 · DISCOVER HOW YOU LEARN',
      title: 'The CogniPrint Diagnostic',
      body: 'The CogniPrint diagnostic has two tiers. The free tier (2 minutes) maps you across 4 core dimensions: content format preference, learning approach, depth tolerance, and sequencing preference. The paid tier (5 minutes) adds 4 advanced dimensions: information density preference, motivational driver, viewpoint range, and prior knowledge anchor. No two CogniPrint profiles are identical — just like no two fingerprints are.',
      visual: <DiagnosticVisual />,
    },
    {
      icon: BookOpen, number: '02', accent: 'mixed',
      overline: 'STEP 2 · UPLOAD YOUR CONTENT',
      title: 'Your Textbook, Your Terms',
      body: 'When you upload a document, CogniPrint\'s AI extracts every key concept, identifies how they relate to each other, and builds a knowledge graph — a structured map of the material. If you upload multiple documents, the graph connects them. Your auditing guide and financial reporting standard become cross-referenced, not siloed.',
      visual: <UploadVisual />,
    },
    {
      icon: Sparkles, number: '03', accent: 'gold',
      overline: 'STEP 3 · LEARN YOUR WAY',
      title: 'AI-Powered. Personally Yours.',
      body: 'Your learning path isn\'t a summary. It\'s a restructured experience: concept maps for spatial thinkers, worked examples for hands-on learners, structured frameworks for top-down processors. Comprehension checkpoints test application-level understanding, not recall. And Professor Cogni is available at every step — contextual, adaptive, and shaped by your CogniPrint.',
      visual: <LearnVisual />,
    },
  ];

  return (
    <section className="relative py-20 px-4 overflow-hidden">
      <Aurora palette="mixed" intensity={0.5} />
      <div className="max-w-6xl mx-auto relative space-y-24">
        {steps.map((step, i) => (
          <div key={step.number} className={`grid lg:grid-cols-12 gap-10 items-center ${i % 2 === 1 ? 'lg:[direction:rtl]' : ''}`}>
            <Reveal variants={i % 2 === 1 ? fadeLeftItem : fadeRightItem} className="lg:col-span-6 [direction:ltr]">
              <div className="flex items-center gap-4 mb-4">
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center shrink-0 ${
                  step.accent === 'gold' ? 'bg-gradient-to-br from-gold-400 to-gold-600 shadow-glow-gold'
                  : step.accent === 'mixed' ? 'bg-gradient-to-br from-indigo-500 to-gold-500'
                  : 'bg-gradient-to-br from-indigo-500 to-indigo-700 shadow-glow-indigo'
                }`}>
                  <step.icon size={24} className="text-white" />
                </div>
                <div className="font-heading font-extrabold text-5xl text-indigo-500/30 leading-none select-none">{step.number}</div>
              </div>
              <p className="text-[10px] font-body font-bold tracking-[0.16em] uppercase text-gold-400 mb-2">{step.overline}</p>
              <h2 className="font-heading font-semibold text-2xl sm:text-3xl mb-4 leading-tight">{step.title}</h2>
              <p className="text-content-secondary leading-relaxed font-body">{step.body}</p>
            </Reveal>
            <Reveal variants={i % 2 === 1 ? fadeRightItem : fadeLeftItem} className="lg:col-span-6 [direction:ltr]">
              <MagneticCard glow={step.accent}>
                <div className="rounded-2xl glass-surface p-6">{step.visual}</div>
              </MagneticCard>
            </Reveal>
          </div>
        ))}
      </div>
    </section>
  );
}

function DiagnosticVisual() {
  const dims = [
    { label: 'Modality', val: 0.85, paid: false },
    { label: 'Depth', val: 0.92, paid: false },
    { label: 'Sequencing', val: 0.6, paid: false },
    { label: 'Approach', val: 0.7, paid: false },
    { label: 'Density', val: 0.5, paid: true },
    { label: 'Motivation', val: 0.78, paid: true },
    { label: 'Viewpoint', val: 0.65, paid: true },
    { label: 'Prior knowledge', val: 0.4, paid: true },
  ];
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <span className="font-heading font-semibold text-sm">8-dimension profile</span>
        <span className="text-[10px] font-mono text-content-muted">live</span>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
        {dims.map((d, i) => (
          <div key={d.label}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] font-body text-content-secondary">{d.label}</span>
              <span className="text-[10px] font-mono text-content-muted">{Math.round(d.val * 100)}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-dark-bg overflow-hidden">
              <motion.div
                className={`h-full rounded-full ${d.paid ? 'bg-gradient-to-r from-content-dim to-content-muted' : 'bg-gradient-to-r from-indigo-500 to-gold-500'}`}
                initial={{ width: 0 }}
                whileInView={{ width: `${d.val * 100}%` }}
                viewport={{ once: true }}
                transition={{ duration: 0.9, delay: i * 0.06 }}
              />
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 flex items-center gap-3 text-[10px] font-body">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-indigo-500" /> <span className="text-content-muted">Free (4)</span></span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-content-muted" /> <span className="text-content-muted">Paid unlock (4)</span></span>
      </div>
    </div>
  );
}

function UploadVisual() {
  const concepts = ['Asset', 'Depreciation', 'Salvage value', 'Useful life', 'Cash flow'];
  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg bg-indigo-500/15 flex items-center justify-center"><BookOpen size={18} className="text-indigo-300" /></div>
        <div className="flex-1">
          <div className="text-sm font-heading font-semibold">Financial Reporting.pdf</div>
          <div className="text-[10px] font-mono text-content-muted">2.4 MB · 84 pages</div>
        </div>
        <span className="px-2 py-0.5 rounded-pill text-[10px] font-semibold bg-success/15 text-success">parsed</span>
      </div>
      <div className="rounded-lg bg-dark-bg border border-dark-border p-4 relative overflow-hidden">
        <div className="text-[10px] font-body font-bold tracking-wider uppercase text-content-muted mb-3">Knowledge graph</div>
        <svg viewBox="0 0 320 140" className="w-full">
          <defs>
            <radialGradient id="node-glow" cx="50%" cy="50%" r="50%">
              <stop offset="0" stopColor="#6C5CE7" stopOpacity="1" />
              <stop offset="1" stopColor="#6C5CE7" stopOpacity="0" />
            </radialGradient>
          </defs>
          {[
            { x1: 60, y1: 70, x2: 140, y2: 30 },
            { x1: 60, y1: 70, x2: 140, y2: 110 },
            { x1: 140, y1: 30, x2: 220, y2: 60 },
            { x1: 140, y1: 110, x2: 220, y2: 90 },
            { x1: 220, y1: 60, x2: 280, y2: 100 },
          ].map((line, i) => (
            <motion.line
              key={i}
              x1={line.x1} y1={line.y1} x2={line.x2} y2={line.y2}
              stroke="#6C5CE7" strokeWidth="1" strokeDasharray="2 3" strokeOpacity="0.5"
              initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 + i * 0.1 }}
            />
          ))}
          {[
            { cx: 60, cy: 70, label: 'Asset' },
            { cx: 140, cy: 30, label: 'Depreciation' },
            { cx: 140, cy: 110, label: 'Useful life' },
            { cx: 220, cy: 60, label: 'Salvage' },
            { cx: 220, cy: 90, label: 'Method' },
            { cx: 280, cy: 100, label: 'Cash flow' },
          ].map((n, i) => (
            <motion.g
              key={n.label}
              initial={{ scale: 0, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 + i * 0.1, type: 'spring', stiffness: 200 }}
              style={{ transformOrigin: `${n.cx}px ${n.cy}px` }}
            >
              <circle cx={n.cx} cy={n.cy} r="6" fill="#6C5CE7" />
              <circle cx={n.cx} cy={n.cy} r="10" fill="none" stroke="#F0A500" strokeOpacity="0.5" strokeWidth="1.5" strokeDasharray="3 2" />
              <text x={n.cx} y={n.cy + 22} fill="#9B97B0" fontSize="8" textAnchor="middle" fontFamily="JetBrains Mono">{n.label}</text>
            </motion.g>
          ))}
        </svg>
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {concepts.map((c) => (
          <span key={c} className="px-2 py-0.5 rounded-pill text-[10px] font-body bg-dark-bg border border-dark-border text-content-muted">{c}</span>
        ))}
      </div>
    </div>
  );
}

function LearnVisual() {
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <span className="font-heading font-semibold text-sm">Path: Depreciation</span>
        <span className="px-2 py-0.5 rounded-pill text-[10px] font-semibold bg-gold-500/15 text-gold-400">your style</span>
      </div>
      <div className="space-y-2.5">
        {[
          { label: 'Concept map: How the bucket leaks', icon: Network, status: 'now' },
          { label: 'Worked example: Year 1–5', icon: Sparkles, status: 'next' },
          { label: 'Test Me: Apply to a new asset', icon: Brain, status: 'queued' },
          { label: 'Connect: Tax implications', icon: ArrowRight, status: 'queued' },
        ].map((row, i) => (
          <motion.div
            key={row.label}
            initial={{ opacity: 0, x: -10 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.12 }}
            className={`flex items-center gap-3 p-3 rounded-lg border ${row.status === 'now' ? 'bg-indigo-500/8 border-indigo-500/30' : 'bg-dark-bg border-dark-border'}`}
          >
            <div className={`w-7 h-7 rounded-md flex items-center justify-center ${row.status === 'now' ? 'bg-indigo-500 text-white' : 'bg-dark-border text-content-muted'}`}>
              <row.icon size={14} />
            </div>
            <span className="flex-1 text-xs font-body text-content-secondary">{row.label}</span>
            <span className="text-[10px] font-mono uppercase text-content-muted">{row.status}</span>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

/* ─── ADDITIONAL FEATURES (alternating with mockups) ─── */
type Feat = {
  icon: typeof Fingerprint;
  overline: string;
  title: string;
  body: string;
  footnote?: string;
  visual?: React.ReactNode;
  palette: 'indigo' | 'gold' | 'mixed';
};
function AdditionalFeaturesSection() {
  const feats: Feat[] = [
    {
      icon: Sparkles, palette: 'indigo',
      overline: 'SAME TEXTBOOK. DIFFERENT EXPERIENCE.',
      title: 'Content That Transforms for You',
      body: 'Upload a chapter once. CogniPrint\'s AI extracts the concepts, maps the relationships, and rebuilds the material into a learning path designed for your CogniPrint profile.',
      visual: <TransformVisual />,
    },
    {
      icon: Smartphone, palette: 'mixed',
      overline: 'PHONE. LAPTOP. BETWEEN CLASSES OR AFTER WORK.',
      title: 'Works Where You Do',
      body: 'The full CogniPrint experience works on your phone. Review concept maps during your commute. Chat with your Tutor while waiting for a meeting. Pick up exactly where you left off, on any device.',
      visual: <DevicesVisual />,
    },
    {
      icon: RefreshCw, palette: 'gold',
      overline: 'RECALIBRATION. BUILT IN. NEVER SILENT.',
      title: 'The Learning Path That Adapts Every Two Weeks',
      body: 'Your CogniPrint profile is not a one-time diagnostic. Every two weeks, CogniPrint reviews how you actually studied — which concepts stuck, which Tutor modes served you best — and quietly updates your profile.',
      footnote: 'Around Day 14, you receive a short, no-pressure preview of your first recalibration. A clean monthly report follows. Pro and Teams members get this included. Coach add-on members ($7.99/month on Pro) get a live bi-weekly review plus unlimited ad-hoc check-ins.',
      visual: <RecalibrationTimeline />,
    },
    {
      icon: Headphones, palette: 'indigo',
      overline: 'LECTURES. PODCASTS. VOICE MEMOS. TRANSCRIBED ON YOUR DEVICE.',
      title: 'Learn From Anything You Can Hear',
      body: 'Most of university and professional study is spoken, not written. CogniPrint accepts audio the same way it accepts a PDF. Drop in a lecture recording or hit record on your phone, and CogniPrint transcribes it locally — your audio never leaves your device when the local engine is active.',
      footnote: 'Supports seven common audio formats up to two hours per file. A one-hour lecture transcribes in about a minute on recent hardware.',
      visual: <AudioVisual />,
    },
    {
      icon: Mic, palette: 'mixed',
      overline: 'VOICE CONVERSATIONS WITH PROFESSOR COGNI. HANDS-FREE. HEADPHONES-READY.',
      title: 'Study Out Loud',
      body: 'Reviewing on a walk. Cooking while preparing for a final. Driving between meetings. Professor Cogni supports full voice conversation across all five interaction modes — Explain, Go Deeper, Test Me, Connect, and Simplify.',
      footnote: 'Voice is optional and never required. Every spoken exchange is mirrored as text, and captions are available when you need them.',
      visual: <VoiceVisual />,
    },
    {
      icon: Network, palette: 'gold',
      overline: 'A VISUAL MAP OF EVERY CONCEPT IN YOUR MATERIAL.',
      title: 'See How Everything Connects',
      body: 'Upload a textbook chapter and a set of lecture notes and CogniPrint builds a single interactive knowledge graph across both. A gold mastery ring fills in around each node as you are tested on it.',
      footnote: 'The graph flags where your sources disagree. Gaps — concepts referenced but never explained — show up as ghosted nodes so you know what to go looking for.',
      visual: <KnowledgeGraphVisual />,
    },
    {
      icon: Trophy, palette: 'indigo',
      overline: 'STREAKS. ACHIEVEMENTS. A PROGRESS DASHBOARD SHAPED TO YOU.',
      title: 'Progress You Can Feel',
      body: 'Every study session, checkpoint passed, and concept mastered earns XP. A daily study streak builds across the calendar. Achievements unlock at meaningful milestones.',
      footnote: 'CogniPrint knows whether gamification motivates you. Extrinsic learners see XP and leaderboards prominently. Intrinsic learners get a quieter interface centred on knowledge-graph mastery.',
      visual: <GamificationVisual />,
    },
    {
      icon: Users, palette: 'mixed',
      overline: 'FOR CLASSES, STUDY GROUPS, AND CORPORATE COHORTS.',
      title: 'Cohort Leaderboards For Teams',
      body: 'Teams tier members get a weekly cohort leaderboard that resets every Monday. Instructors see aggregate mastery and engagement; learners see their own position and can opt out entirely. Cohort challenges reward team wins.',
      visual: <CohortVisual />,
    },
  ];

  return (
    <section className="relative py-20 px-4 overflow-hidden">
      <Aurora palette="mixed" intensity={0.5} />
      <div className="max-w-6xl mx-auto relative">
        <Reveal className="text-center mb-16">
          <span className="inline-block mb-4 px-3 py-1 rounded-pill text-xs font-semibold bg-gold-500/12 text-gold-400 border border-gold-500/30">
            More that's inside
          </span>
          <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-3">Every feature shaped by your profile.</h2>
          <p className="text-content-secondary font-body max-w-2xl mx-auto">
            The features that didn't make the landing page cut. Each one ships at the tier where it lives.
          </p>
        </Reveal>

        <div className="space-y-24">
          {feats.map((f, i) => (
            <div key={f.title} className={`grid lg:grid-cols-12 gap-10 items-center ${i % 2 === 1 ? 'lg:[direction:rtl]' : ''}`}>
              <Reveal variants={i % 2 === 1 ? fadeLeftItem : fadeRightItem} className="lg:col-span-6 [direction:ltr]">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-5 ${
                  f.palette === 'gold' ? 'bg-gradient-to-br from-gold-400 to-gold-600 shadow-glow-gold'
                  : f.palette === 'mixed' ? 'bg-gradient-to-br from-indigo-500 to-gold-500'
                  : 'bg-gradient-to-br from-indigo-500 to-indigo-700 shadow-glow-indigo'
                }`}>
                  <f.icon size={22} className="text-white" />
                </div>
                <p className="text-[10px] font-body font-bold tracking-[0.16em] uppercase text-gold-400 mb-2">{f.overline}</p>
                <h3 className="font-heading font-semibold text-2xl sm:text-3xl mb-4 leading-tight">{f.title}</h3>
                <p className="text-content-secondary leading-relaxed font-body">{f.body}</p>
                {f.footnote ? (
                  <div className="mt-5 inline-flex items-start gap-2 px-4 py-3 rounded-lg bg-gold-500/10 border border-gold-500/25 text-gold-400 font-body text-sm leading-relaxed">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-gold-500 shrink-0" />
                    <span>{f.footnote}</span>
                  </div>
                ) : null}
              </Reveal>
              {f.visual ? (
                <Reveal variants={i % 2 === 1 ? fadeRightItem : fadeLeftItem} className="lg:col-span-6 [direction:ltr]">
                  <MagneticCard glow={f.palette}>
                    <div className="rounded-2xl glass-surface p-6">{f.visual}</div>
                  </MagneticCard>
                </Reveal>
              ) : null}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TransformVisual() {
  return (
    <div className="grid grid-cols-3 gap-3">
      {[
        { tag: 'Visual', label: 'Concept map', icon: Network, color: 'indigo' },
        { tag: 'Hands-on', label: 'Worked example', icon: Sparkles, color: 'gold' },
        { tag: 'Top-down', label: 'Structured frame', icon: Brain, color: 'mixed' },
      ].map((card, i) => (
        <motion.div
          key={card.tag}
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: i * 0.12 }}
          className="rounded-lg bg-dark-bg border border-dark-border p-3 text-center"
        >
          <div className={`w-9 h-9 mx-auto mb-2 rounded-lg flex items-center justify-center ${
            card.color === 'gold' ? 'bg-gold-500/15 text-gold-400'
            : card.color === 'mixed' ? 'bg-gradient-to-br from-indigo-500/20 to-gold-500/20 text-indigo-300'
            : 'bg-indigo-500/15 text-indigo-300'
          }`}>
            <card.icon size={16} />
          </div>
          <div className="text-[10px] font-body font-bold tracking-wider uppercase text-content-muted mb-1">{card.tag}</div>
          <div className="text-xs font-body text-content">{card.label}</div>
        </motion.div>
      ))}
    </div>
  );
}

function DevicesVisual() {
  return (
    <div className="flex items-end justify-center gap-4 py-2">
      {/* Phone */}
      <motion.div
        initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
        className="w-20 h-36 rounded-xl border-2 border-dark-border bg-dark-bg p-1.5 relative"
      >
        <div className="h-full rounded-md bg-gradient-to-b from-indigo-500/15 to-gold-500/10 flex flex-col items-center justify-center gap-1.5">
          <div className="w-8 h-8 rounded-full bg-indigo-500/20 flex items-center justify-center"><Brain size={14} className="text-indigo-300" /></div>
          <div className="w-10 h-1 rounded bg-indigo-500/40" />
          <div className="w-8 h-1 rounded bg-indigo-500/30" />
        </div>
      </motion.div>
      {/* Laptop */}
      <motion.div
        initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.15 }}
        className="flex flex-col items-center"
      >
        <div className="w-44 h-28 rounded-t-lg border-2 border-b-0 border-dark-border bg-dark-bg p-2">
          <div className="h-full rounded-md bg-gradient-to-br from-indigo-500/15 via-gold-500/10 to-indigo-500/15 flex items-center justify-center gap-1">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="w-1 rounded bg-indigo-400/60" style={{ height: 10 + i * 6 }} />
            ))}
          </div>
        </div>
        <div className="w-52 h-1.5 bg-dark-border rounded-b-lg" />
      </motion.div>
      {/* Tablet */}
      <motion.div
        initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 }}
        className="w-24 h-32 rounded-xl border-2 border-dark-border bg-dark-bg p-1.5"
      >
        <div className="h-full rounded-md bg-gradient-to-b from-gold-500/12 to-indigo-500/10 flex flex-col items-center justify-center gap-1.5">
          <div className="w-8 h-8 rounded-full bg-gold-500/20 flex items-center justify-center"><RefreshCw size={14} className="text-gold-400" /></div>
          <div className="w-12 h-1 rounded bg-gold-500/40" />
        </div>
      </motion.div>
    </div>
  );
}

function RecalibrationTimeline() {
  const points = [
    { label: 'Day 0', sub: 'Diagnostic', glow: false },
    { label: 'Day 14', sub: 'Confidence Check', glow: true },
    { label: 'Day 28', sub: 'Recalibration', glow: false },
    { label: 'Day 42', sub: 'Recalibration', glow: false },
  ];
  return (
    <div>
      <div className="font-heading font-semibold text-sm mb-5">Bi-weekly cadence</div>
      <div className="relative">
        <div className="absolute left-3 top-3 bottom-3 w-px bg-gradient-to-b from-indigo-500/40 via-gold-500/40 to-indigo-500/40" />
        <div className="space-y-4 pl-8">
          {points.map((p, i) => (
            <motion.div
              key={p.label}
              initial={{ opacity: 0, x: -10 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.12 }}
              className="relative"
            >
              <div className={`absolute -left-[26px] top-1 w-3 h-3 rounded-full ${p.glow ? 'bg-gold-500 shadow-glow-gold' : 'bg-indigo-500'}`} />
              <div className="text-xs font-mono text-content-muted">{p.label}</div>
              <div className="text-sm font-body text-content">{p.sub}</div>
            </motion.div>
          ))}
        </div>
      </div>
      <div className="mt-5 rounded-lg p-3 bg-gold-500/8 border border-gold-500/20 text-[11px] font-body text-content-secondary leading-relaxed">
        Coach add-on turns each 2-week point into a written conversation tied to your CogniPrint.
      </div>
    </div>
  );
}

function AudioVisual() {
  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-indigo-500/15 flex items-center justify-center"><Headphones size={18} className="text-indigo-300" /></div>
        <div className="flex-1">
          <div className="text-sm font-heading font-semibold">Lecture_W4_Depreciation.mp3</div>
          <div className="text-[10px] font-mono text-content-muted">58 min · transcribing locally</div>
        </div>
      </div>
      <div className="rounded-lg bg-dark-bg border border-dark-border p-4">
        <div className="flex items-end gap-1 h-12 mb-3">
          {Array.from({ length: 32 }).map((_, i) => (
            <motion.div
              key={i}
              initial={{ height: 4 }}
              animate={{ height: [6, 10 + (i % 5) * 6, 6] }}
              transition={{ duration: 1.2 + (i % 3) * 0.3, repeat: Infinity, ease: 'easeInOut' }}
              className="flex-1 rounded-sm bg-gradient-to-t from-indigo-500 to-indigo-300"
            />
          ))}
        </div>
        <div className="flex items-center justify-between text-[10px] font-mono text-content-muted">
          <span>local engine · on-device</span>
          <span className="text-success">●  64%</span>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {['MP3', 'WAV', 'M4A', 'OGG', 'FLAC', 'WebM', 'AAC'].map((f) => (
          <span key={f} className="px-2 py-0.5 rounded-pill text-[10px] font-mono bg-dark-bg border border-dark-border text-content-muted">{f}</span>
        ))}
      </div>
    </div>
  );
}

function VoiceVisual() {
  const modes = ['Explain', 'Go Deeper', 'Test Me', 'Connect', 'Simplify'];
  return (
    <div>
      <div className="flex items-center gap-3 mb-5">
        <motion.div
          animate={{ scale: [1, 1.08, 1], boxShadow: ['0 0 0 0 rgba(108,92,231,0.45)', '0 0 0 10px rgba(108,92,231,0)', '0 0 0 0 rgba(108,92,231,0)'] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center"
        >
          <Mic size={18} className="text-white" />
        </motion.div>
        <div>
          <div className="font-heading font-semibold text-sm">Listening…</div>
          <div className="text-[10px] font-mono text-content-muted">"explain depreciation differently"</div>
        </div>
      </div>
      <div className="space-y-2 mb-4">
        <div className="rounded-lg bg-dark-bg border border-dark-border p-3 text-xs font-body text-content-secondary leading-relaxed">
          Picture an asset as a leaking bucket. Each year a slice drips out — that drip is depreciation.
        </div>
        <div className="text-[10px] font-mono text-content-muted text-right">caption · 1.2× speed · indigo voice</div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {modes.map((m, i) => (
          <span key={m} className={`px-2.5 py-1 rounded-pill text-[10px] font-body ${i === 0 ? 'bg-indigo-500 text-white' : 'bg-dark-bg border border-dark-border text-content-muted'}`}>{m}</span>
        ))}
      </div>
    </div>
  );
}

function KnowledgeGraphVisual() {
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <span className="font-heading font-semibold text-sm">Cross-document graph</span>
        <span className="text-[10px] font-mono text-content-muted">2 sources · 1 contradiction</span>
      </div>
      <div className="rounded-lg bg-dark-bg border border-dark-border p-4">
        <svg viewBox="0 0 320 180" className="w-full">
          {[
            { x1: 50, y1: 90, x2: 130, y2: 50 },
            { x1: 50, y1: 90, x2: 130, y2: 130 },
            { x1: 130, y1: 50, x2: 220, y2: 80 },
            { x1: 130, y1: 130, x2: 220, y2: 80 },
            { x1: 220, y1: 80, x2: 290, y2: 60 },
            { x1: 220, y1: 80, x2: 290, y2: 110 },
          ].map((line, i) => (
            <motion.line
              key={i} {...line}
              stroke={i === 2 ? '#FF6B6B' : '#6C5CE7'} strokeWidth={i === 2 ? '1.5' : '1'} strokeDasharray={i === 2 ? '4 2' : '2 3'} strokeOpacity="0.6"
              initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.1 + i * 0.08 }}
            />
          ))}
          {[
            { cx: 50, cy: 90, mastery: 1, label: 'Asset' },
            { cx: 130, cy: 50, mastery: 0.7, label: 'Depreciation' },
            { cx: 130, cy: 130, mastery: 0.5, label: 'Useful life' },
            { cx: 220, cy: 80, mastery: 0.3, label: 'Method' },
            { cx: 290, cy: 60, mastery: 0, label: 'Salvage', ghost: true },
            { cx: 290, cy: 110, mastery: 0.6, label: 'Cash flow' },
          ].map((n, i) => (
            <motion.g
              key={n.label}
              initial={{ scale: 0, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 + i * 0.08, type: 'spring', stiffness: 200 }}
              style={{ transformOrigin: `${n.cx}px ${n.cy}px` }}
            >
              <circle cx={n.cx} cy={n.cy} r="6" fill={n.ghost ? 'transparent' : '#6C5CE7'} stroke={n.ghost ? '#6C6880' : 'none'} strokeWidth="1" strokeDasharray={n.ghost ? '2 2' : ''} />
              {n.mastery > 0 ? (
                <circle cx={n.cx} cy={n.cy} r="10" fill="none" stroke="#F0A500" strokeWidth="1.5" strokeOpacity={n.mastery} />
              ) : null}
              <text x={n.cx} y={n.cy + 22} fill={n.ghost ? '#6C6880' : '#9B97B0'} fontSize="8" textAnchor="middle" fontFamily="JetBrains Mono">{n.label}</text>
            </motion.g>
          ))}
        </svg>
        <div className="flex items-center gap-3 text-[10px] font-body mt-2">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-indigo-500" /> <span className="text-content-muted">concept</span></span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full ring-1 ring-gold-500" /> <span className="text-content-muted">mastery</span></span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full border border-content-dim" /> <span className="text-content-muted">gap</span></span>
          <span className="flex items-center gap-1"><span className="w-3 h-px bg-error" /> <span className="text-content-muted">contradiction</span></span>
        </div>
      </div>
    </div>
  );
}

function GamificationVisual() {
  return (
    <div>
      <div className="grid grid-cols-3 gap-3 mb-4">
        {[
          { label: 'Streak', value: '14d', icon: '🔥', tone: 'gold' },
          { label: 'XP', value: '2,480', icon: '⚡', tone: 'indigo' },
          { label: 'Mastered', value: '37', icon: '★', tone: 'mixed' },
        ].map((s, i) => (
          <motion.div
            key={s.label}
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1 }}
            className="rounded-lg bg-dark-bg border border-dark-border p-3 text-center"
          >
            <div className="text-lg mb-0.5">{s.icon}</div>
            <div className={`font-heading font-bold text-lg ${s.tone === 'gold' ? 'text-gold-400' : s.tone === 'mixed' ? 'text-indigo-300' : 'text-content'}`}>{s.value}</div>
            <div className="text-[10px] font-body text-content-muted">{s.label}</div>
          </motion.div>
        ))}
      </div>
      <div className="rounded-lg p-3 bg-indigo-500/8 border border-indigo-500/20">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] font-body font-bold tracking-wider uppercase text-indigo-300">Motivation-aware</div>
          <div className="text-[10px] font-mono text-content-muted">your driver: extrinsic</div>
        </div>
        <p className="text-xs font-body text-content-secondary leading-relaxed">
          Intrinsic learners see this panel collapsed by default and the interface centres on knowledge-graph mastery instead.
        </p>
      </div>
    </div>
  );
}

function CohortVisual() {
  const rows = [
    { rank: 1, name: 'A. Khumalo', xp: 4280, you: false },
    { rank: 2, name: 'You', xp: 4120, you: true },
    { rank: 3, name: 'M. Naidoo', xp: 3960, you: false },
    { rank: 4, name: 'L. Smit', xp: 3540, you: false },
  ];
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <span className="font-heading font-semibold text-sm">FIN-301 cohort · this week</span>
        <span className="text-[10px] font-mono text-content-muted">resets Mon</span>
      </div>
      <div className="space-y-1.5">
        {rows.map((r, i) => (
          <motion.div
            key={r.rank}
            initial={{ opacity: 0, x: -8 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1 }}
            className={`flex items-center gap-3 p-2.5 rounded-lg ${r.you ? 'bg-indigo-500/12 border border-indigo-500/30' : 'bg-dark-bg border border-dark-border'}`}
          >
            <span className={`w-6 h-6 rounded-md flex items-center justify-center text-[11px] font-mono font-bold ${r.you ? 'bg-indigo-500 text-white' : 'bg-dark-border text-content-muted'}`}>{r.rank}</span>
            <span className={`flex-1 text-xs font-body ${r.you ? 'text-content font-semibold' : 'text-content-secondary'}`}>{r.name}</span>
            <span className="text-[11px] font-mono text-content-muted">{r.xp.toLocaleString()} XP</span>
          </motion.div>
        ))}
      </div>
      <p className="mt-3 text-[10px] font-body text-content-muted text-center">
        Every learner can opt out of the leaderboard entirely.
      </p>
    </div>
  );
}

/* ─── COMPARISON (modern card grid) ─── */
function ComparisonSection() {
  const rows = [
    { capability: 'Learner profiling', typical: 'None', cogniprint: '4-dimension Free / Full 8-dimension on Paid', tone: 'differ' },
    { capability: 'Content processing', typical: 'Generic summaries', cogniprint: 'Personalised learning paths', tone: 'differ' },
    { capability: 'AI assistant', typical: 'General chatbot', cogniprint: 'Contextual Tutor shaped by your profile', tone: 'differ' },
    { capability: 'Adaptation', typical: 'Static output', cogniprint: 'Continuous recalibration as you learn', tone: 'win' },
    { capability: 'Comprehension testing', typical: 'Basic quizzes', cogniprint: 'Profile-matched checkpoints', tone: 'differ' },
    { capability: 'Adapts after initial setup', typical: 'Treats your first diagnostic as final', cogniprint: 'Recalibrates every two weeks against how you actually studied', tone: 'win' },
    { capability: 'Audio & lecture support', typical: 'Most accept text only', cogniprint: 'Transcribes lectures on your device, feeds the same pipeline', tone: 'win' },
    { capability: 'Voice conversations with the AI', typical: 'One-way audio overviews', cogniprint: 'Bidirectional, profile-adaptive, all 5 modes', tone: 'win' },
    { capability: 'Visual knowledge graph', typical: 'One document at a time', cogniprint: 'Single graph across sources, flags contradictions', tone: 'win' },
    { capability: 'Motivation-aware gamification', typical: 'Uniform gamification', cogniprint: 'Adjusts visibility to your motivational driver', tone: 'win' },
    { capability: 'Human coach optionally in the loop', typical: 'Stops at software', cogniprint: 'Coach add-on layered on top of the AI', tone: 'win' },
  ];

  return (
    <section className="relative py-20 px-4 overflow-hidden">
      <Aurora palette="cool" intensity={0.4} />
      <div className="max-w-6xl mx-auto relative">
        <Reveal className="text-center mb-12 max-w-3xl mx-auto">
          <span className="inline-block mb-4 px-3 py-1 rounded-pill text-xs font-semibold bg-indigo-500/12 text-indigo-300 border border-indigo-500/30">
            How CogniPrint compares
          </span>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl mb-3 leading-tight">
            Other tools summarise your content. <span className="bg-gradient-to-r from-indigo-400 to-gold-500 bg-clip-text text-transparent">CogniPrint transforms it for your brain.</span>
          </h2>
          <p className="text-content-secondary font-body">
            The difference isn't incremental. It's structural. Other tools treat every learner the same — CogniPrint starts by understanding who you are.
          </p>
        </Reveal>

        <div className="grid lg:grid-cols-12 gap-4 mb-6 px-2">
          <div className="lg:col-span-3 text-[10px] font-body font-bold tracking-wider uppercase text-content-muted">Capability</div>
          <div className="lg:col-span-4 text-[10px] font-body font-bold tracking-wider uppercase text-content-muted">Typical AI Study Tools</div>
          <div className="lg:col-span-5 text-[10px] font-body font-bold tracking-wider uppercase text-indigo-300 flex items-center gap-1.5">
            <Sparkles size={11} /> CogniPrint
          </div>
        </div>

        <StaggerGroup className="space-y-2">
          {rows.map((row) => (
            <motion.div key={row.capability} variants={fadeUpItem}>
              <div className="grid lg:grid-cols-12 gap-4 rounded-lg border border-dark-border bg-dark-surface/40 hover:bg-dark-surface/70 hover:border-dark-border-light transition-colors p-4 group">
                <div className="lg:col-span-3 text-sm font-body font-semibold text-content">{row.capability}</div>
                <div className="lg:col-span-4 flex items-start gap-2 text-sm font-body text-content-muted">
                  <X size={14} className="text-error/70 mt-0.5 shrink-0" />
                  <span>{row.typical}</span>
                </div>
                <div className={`lg:col-span-5 flex items-start gap-2 text-sm font-body rounded-md p-2.5 -m-2.5 ${row.tone === 'win' ? 'bg-indigo-500/8 border border-indigo-500/25 group-hover:bg-indigo-500/12' : 'bg-indigo-500/5 border border-indigo-500/15'}`}>
                  <Check size={14} className="text-success mt-0.5 shrink-0" />
                  <span className="text-content">{row.cogniprint}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </StaggerGroup>
      </div>
    </section>
  );
}

/* ─── FAQ ─── */
function FaqSection() {
  return (
    <section className="relative py-20 px-4 overflow-hidden">
      <Aurora palette="indigo" intensity={0.4} />
      <div className="max-w-3xl mx-auto relative">
        <Reveal className="text-center mb-10">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl mb-3">Frequently asked questions</h2>
          <p className="text-content-secondary font-body">Everything visitors ask before they sign up.</p>
        </Reveal>
        <Reveal>
          <Accordion items={fullFaq} />
        </Reveal>
      </div>
    </section>
  );
}

/* ─── FINAL CTA ─── */
function FinalCta() {
  return (
    <section className="relative py-24 px-4 overflow-hidden">
      <RidgeTexture opacity="opacity-[0.07]" />
      <Aurora palette="mixed" intensity={0.9} />
      <div className="max-w-4xl mx-auto relative">
        <Reveal variants={scaleItem}>
          <GlowBorder rounded="rounded-2xl" className="bg-dark-surface/70 backdrop-blur-md p-8 sm:p-14 text-center">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl mb-8 leading-tight">
              Your brain has a fingerprint.
              <br />
              <span className="bg-gradient-to-r from-indigo-400 to-gold-500 bg-clip-text text-transparent">
                It's time your study tools knew what it looked like.
              </span>
            </h2>
            <div className="flex flex-col items-center gap-6">
              <Link to="/signup">
                <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.98 }} className="inline-block">
                  <Button variant="gold" size="lg">
                    Start free — 2 minutes
                    <ArrowRight size={16} />
                  </Button>
                </motion.div>
              </Link>
              <Link to="/pricing" className="text-sm font-body text-indigo-400 hover:text-indigo-300 transition-colors inline-flex items-center gap-1">
                See pricing <ArrowRight size={14} />
              </Link>
            </div>
          </GlowBorder>
        </Reveal>
      </div>
    </section>
  );
}

/* ─── PAGE ─── */
export function HowItWorksPage() {
  return (
    <div className="min-h-screen bg-dark-bg">
      <HeroSection />
      <SectionDivider />
      <StepsSection />
      <SectionDivider />
      <AdditionalFeaturesSection />
      <SectionDivider />
      <ComparisonSection />
      <SectionDivider />
      <FaqSection />
      <SectionDivider />
      <FinalCta />
      <Footer />
    </div>
  );
}

void Button;
